import lunr from "lunr"
import { useMemo } from "react"
import type { SearchIndexData } from "./useDocs"

export interface SearchResult {
  path: string
  title: string
  snippet: string
  score: number
}

function extractSnippet(
  content: string,
  query: string,
  maxLength = 120,
): string {
  const lowerContent = content.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const terms = lowerQuery.split(/\s+/).filter((t) => t.length > 0)

  let bestIndex = -1
  for (const term of terms) {
    const idx = lowerContent.indexOf(term)
    if (idx !== -1) {
      bestIndex = idx
      break
    }
  }

  if (bestIndex === -1) {
    return content.slice(0, maxLength) + (content.length > maxLength ? "…" : "")
  }

  const start = Math.max(0, bestIndex - Math.floor(maxLength / 3))
  const end = Math.min(content.length, start + maxLength)

  let snippet = content.slice(start, end)
  if (start > 0) snippet = "…" + snippet
  if (end < content.length) snippet = snippet + "…"

  return snippet
}

export function useDocsSearch(searchData: SearchIndexData | undefined): {
  search: (query: string) => SearchResult[]
  isIndexReady: boolean
} {
  const { index, documents } = useMemo(() => {
    if (!searchData) {
      return {
        index: null,
        documents: new Map<string, { title: string; content: string }>(),
      }
    }

    const idx = lunr.Index.load(searchData.serializedIndex)
    const docs = new Map(Object.entries(searchData.docs))

    return { index: idx, documents: docs }
  }, [searchData])

  const search = useMemo(() => {
    return (query: string): SearchResult[] => {
      if (!index || !query.trim()) {
        return []
      }

      try {
        const results = index.search(query)
        return results.slice(0, 10).map((result) => {
          const doc = documents.get(result.ref)
          return {
            path: result.ref,
            title: doc?.title ?? result.ref,
            snippet: doc ? extractSnippet(doc.content, query) : "",
            score: result.score,
          }
        })
      } catch {
        const wildcardQuery = query
          .split(/\s+/)
          .filter((t) => t.length > 0)
          .map((t) => `${t}*`)
          .join(" ")
        try {
          const results = index.search(wildcardQuery)
          return results.slice(0, 10).map((result) => {
            const doc = documents.get(result.ref)
            return {
              path: result.ref,
              title: doc?.title ?? result.ref,
              snippet: doc ? extractSnippet(doc.content, query) : "",
              score: result.score,
            }
          })
        } catch {
          return []
        }
      }
    }
  }, [index, documents])

  return {
    search,
    isIndexReady: !!index,
  }
}
